import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import Content from '../components/layout/Content';
import { ControlledTabs } from '../components/basics/Tabs';
import {
  EditFarmCropSettings,
  EditTemplateCropSettings,
} from '../components/templates/EditCropSettings';
import {
  EditFarmCropCarbon,
  EditTemplateCropCarbon,
} from '../components/templates/EditCarbon';
import {
  FarmCropOperations,
  TemplateCropOperations,
} from '../components/templates/CropOperations';
import {
  FarmCropRevenue,
  TemplateCropRevenue,
} from '../components/templates/CropRevenue';
import {
  FarmCropLossEvents,
  TemplateCropLossEvents,
} from '../components/templates/CropLossEvents';
import { getFirstQueryResult } from '../utils/graphql';
import { useSetBreadcrumbEffect } from '../contexts/BreadcrumbContext';
import { UNKNOWN_CROP_NAME } from '../constants/fields';

export const CROP_LAYOUT_TABS = [
  {
    display: 'Crop Settings',
    value: 'edit',
    farmComponent: EditFarmCropSettings,
    templateComponent: EditTemplateCropSettings,
  },
  {
    display: 'Operations',
    value: 'operations',
    farmComponent: FarmCropOperations,
    templateComponent: TemplateCropOperations,
    children: [
      {
        path: ':operationId',
        farmComponent: FarmCropOperations,
        templateComponent: TemplateCropOperations,
      },
    ],
  },
  {
    display: 'Revenue',
    value: 'revenue',
    farmComponent: FarmCropRevenue,
    templateComponent: TemplateCropRevenue,
  },
  {
    display: 'Loss Events',
    value: 'loss',
    farmComponent: FarmCropLossEvents,
    templateComponent: TemplateCropLossEvents,
  },
  {
    display: 'Carbon',
    value: 'carbon',
    farmComponent: EditFarmCropCarbon,
    templateComponent: EditTemplateCropCarbon,
  },
];

const CropTemplateQuery = gql`
  query CropTemplateName($id: ID!) {
    cropTemplate(id: $id) {
      id
      name
    }
  }
`;

const FarmCropQuery = gql`
  query FarmCropName($id: ID!) {
    rawCrop(id: $id) {
      id
      name
    }
  }
`;

const CropHeading = styled.h1`
  margin: var(--header-margin-y) var(--cell-padding);
`;

const CropTabHeader = ({ nameQuery }) => {
  const { id, teamId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { data, loading } = useQuery(nameQuery, { variables: { id } });
  const cropData = getFirstQueryResult(data);
  const breadcrumbs = [{ display: cropData?.name || '...' }];
  if (teamId) {
    breadcrumbs.unshift({
      display: 'Crop Templates',
      url: ':rootUrl/templates',
    });
  }
  useSetBreadcrumbEffect(breadcrumbs, [cropData?.name, teamId]);

  const activeTab = useMemo(() => {
    const pathElements = pathname.split('/');
    // Get the path element after the crop id
    const cropIdIndex = pathElements.indexOf(id);
    return pathElements[cropIdIndex + 1];
  }, [id, pathname]);

  return (
    <div className="mx-2 d-flex flex-column">
      <CropHeading>
        {loading ? '...' : cropData?.name || UNKNOWN_CROP_NAME}
      </CropHeading>
      <ControlledTabs
        tabs={CROP_LAYOUT_TABS}
        activeTab={activeTab}
        onTabChange={(tab) => navigate(`./${tab}`)}
      />
    </div>
  );
};

export const cropTabRoutes = ({ componentAccessor, IndexComponent }) => {
  return [
    {
      index: true,
      element: <IndexComponent />,
    },
    ...CROP_LAYOUT_TABS.map((tab) => {
      const Component = tab[componentAccessor];
      return {
        path: tab.value,
        element: <Component />,
        children: tab.children?.map((childTab) => {
          const ChildComponent = childTab[componentAccessor];
          return {
            path: childTab.path,
            element: <ChildComponent />,
          };
        }),
      };
    }),
  ];
};

const CropTabLayout = ({ nameQuery }) => (
  <>
    <CropTabHeader nameQuery={nameQuery} />
    <Content>
      <Outlet />
    </Content>
  </>
);

export const FarmCropTabLayout = () => (
  <CropTabLayout nameQuery={FarmCropQuery} />
);

export const TemplateCropTabLayout = () => (
  <CropTabLayout nameQuery={CropTemplateQuery} />
);
